import Decimal from 'decimal.js';
import { compact, filter } from 'lodash';

import { EstateWaterfallHypotheticalTransferTransferTaxKind } from '@/types/schema';

import { HypotheticalTransferModalExemption_EstateWaterfallHypotheticalTransferFragment } from '../graphql/HypotheticalTransferModalExemption.generated';
import { AnnuallyRecurringValue } from '../HypotheticalTransferModal.types';

interface PerGrantorNetHypotheticalTransferAmountArgs {
  transfers: HypotheticalTransferModalExemption_EstateWaterfallHypotheticalTransferFragment[];
  grantorId: string;
  transferId: string;
}

/** Gets the net total of hypothetical transfers.  Per product, does not account for
 *  recurring transfers with a growth factor applied.
 */
export function perGrantorNetHypotheticalTransferAmount({
  transfers,
  grantorId,
  transferId,
}: PerGrantorNetHypotheticalTransferAmountArgs): Decimal {
  const hypotheticalTransfers = transferId
    ? compact(filter(transfers, ({ id }) => id !== transferId))
    : transfers;

  return hypotheticalTransfers
    .filter(
      // only taxable transfers use up exemptions
      ({ transferTaxKind }) =>
        transferTaxKind ===
        EstateWaterfallHypotheticalTransferTransferTaxKind.GrantorTaxableGift
    )
    .filter(
      ({ giftingGrantors }) =>
        giftingGrantors?.some(({ id }) => id === grantorId) ?? false
    )
    .reduce<Decimal>(
      (
        acc,
        { transferValue, startYear, endYear, giftingGrantors, taxDiscount }
      ) => {
        if (!transferValue || !giftingGrantors || !giftingGrantors.length)
          return acc;

        let baseTransferAmount = transferValue;
        if (taxDiscount) {
          const discount = baseTransferAmount.times(taxDiscount.div(100));
          baseTransferAmount = baseTransferAmount.minus(discount);
        }

        baseTransferAmount = baseTransferAmount.div(giftingGrantors.length);

        let netTransferAmount: Decimal;
        if (startYear && endYear) {
          const years = endYear - startYear + 1;
          netTransferAmount = baseTransferAmount.times(years);
        } else {
          netTransferAmount = baseTransferAmount;
        }

        return acc.plus(netTransferAmount);
      },
      new Decimal(0)
    );
}

export function getTotalHypotheticalTransferAmount({
  transferAmount,
  transferStartYear,
  transferEndYear,
  transferFrequency,
}: {
  transferAmount: Decimal | null;
  transferStartYear: number;
  transferEndYear?: number | undefined | null;
  transferFrequency: AnnuallyRecurringValue;
}): Decimal {
  if (!transferAmount) {
    return new Decimal(0);
  }

  if (transferFrequency === AnnuallyRecurringValue.false) {
    return transferAmount;
  }

  let netTransferAmount = transferAmount;

  if (transferStartYear && transferEndYear) {
    const years = transferEndYear - transferStartYear + 1;
    netTransferAmount = netTransferAmount.times(years);
  }

  return netTransferAmount;
}
